<template>
  <div class="offer-info">
    <UiConfirm
      ref="confirm"
      :width="480"
      :type="confirmType"
      :action-name="actionName"
      :action="confirmAction"
      :action-icon="actionIcon"
      :action-fa-icon="actionFaIcon"
      @save="onConfirm"
      @close="confirmClose"
    >
      <template v-slot:title>
        <div v-html="confirmTitle" />
      </template>
      <div class="body__wrap">
        <i
          v-if="confirmAction === 'deleteCategoryItem'"
          :class="confirmType"
          class="fas fa-trash-alt t:mr-12"
        />
        <div
          v-if="confirmAction === 'deleteCategoryItem'"
          class="body__msg"
          v-html="confirmMsg"
        />
        <div
          v-else
          class="select-wrap offers-categories__full-width"
        >
          <UiInput
            v-model="category.name"
            :error="getError('name')"
            :placeholder="$t('affiliates.offersCategory.category_name_placeholder')"
            :label="$t('affiliates.offersCategory.category_name_label')"
            size="big"
            autosize
          />
        </div>
      </div>
    </UiConfirm>
    <div class="offer-info__title-wrapper">
      <div class="offer-info__title t:space-x-8">
        <span v-if="category.name">
          {{ category.name }}
        </span>
        <span v-else>
          {{ $t('affiliates.offersCategory.default_offer') }}
        </span>
        <span
          v-if="category.name && canEditCatalog"
          class="offer-info__action"
          @click="editCategoryHandler"
        >
          <UiIcon
            name="pen"
            color="var(--text-primary)"
          />
        </span>
        <span
          v-if="category.name && canEditCatalog"
          class="offer-info__action"
          @click="deleteCategoryHandler"
        >
          <UiIcon
            name="trash-alt"
            color="var(--text-negative)"
          />
        </span>
        <UiInput
          v-model.trim="searchQuery"
          class="offer-info__search t:ml-4"
          :width="180"
          is-search
          @search="getCatalog"
        />
      </div>
      <UiButton
        v-if="canEditCatalog"
        icon="plus"
        @click="addOffer"
      >
        {{ $t('affiliates.offersCategory.create_offer') }}
      </UiButton>
    </div>
    <div
      v-loading="isDataLoading"
      class="offer-info__catalog-wrapper"
    >
      <div
        v-if="catalogs.length === 0"
        class="offer-info__catalog-empty"
      >
        {{ $t('affiliates.offersCategory.no_offers') }}
      </div>
      <div
        v-else
        class="offer-info__catalog"
      >
        <div
          v-for="catalog in catalogs"
          :key="catalog.id"
          :class="{
            'offer-info__catalog-item': true,
            'offer-info__catalog-item--superuser': canEditCatalog
          }"
        >
          <div class="offer-info__catalog-item-wrapper">
            <div class="t:flex t:justify-between">
              <div class="offer-info__catalog-header">
                <span><span class="offer-info__catalog-text-gray">ID:</span>
                  {{ catalog.incrementing_id }}</span>
                <div class="offer-info__catalog-separator t:mr-8 t:ml-8" />
                <span><span class="offer-info__catalog-text-gray">{{ $t('affiliates.offersCategory.category') }}:</span>
                  {{ catalog.category_name }}</span>
              </div>
              <div
                v-if="canEditCatalog"
                class="offer-info__catalog-header-actions"
              >
                <span
                  class="offer-info__action"
                  @click="editCategoryItemHandler(catalog)"
                >
                  <UiIcon
                    name="pen"
                    color="var(--text-primary)"
                  />
                </span>
                <span
                  class="offer-info__action"
                  @click="deleteCategoryItemHandler(catalog)"
                >
                  <UiIcon
                    name="trash-alt"
                    color="var(--text-negative)"
                  />
                </span>
              </div>
            </div>
            <div class="offer-info__catalog-title">
              {{ catalog.name }}
            </div>
            <div
              class="offer-info__catalog-description"
              :title="catalog.description"
            >
              {{ catalog.description }}
            </div>
            <div class="t:flex t:mt-12">
              <div
                v-for="item in availableBrands(
                  catalog.brand_visibility,
                  catalog.visible_brands
                )"
                :key="item.id"
                class="offer-info__catalog-brands"
              >
                <img
                  v-if="item.favicon_base64"
                  class="t:mr-4"
                  :src="item.favicon_base64"
                  :alt="item.name"
                >
                {{ item.name }}
              </div>
            </div>
            <div class="offer-info__catalog-rules t:pt-12 t:mt-12">
              <span class="offer-info__catalog-rules-count">{{ catalog.payout_rules.length }} {{ $t('affiliates.offersCategory.rule_conditions', catalog.payout_rules.length) }}</span>
              <div class="t:flex t:items-center t:cursor-pointer">
                <UiIcon
                  name="eye"
                  color="var(--text-primary)"
                />
                <span
                  class="t:ml-4"
                  @click="handlePreview(catalog)"
                >
                  {{ $t('crm.buttons.view') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <PreviewPopup
      v-model:offer-info="previewPopupData"
      :is-data-loading="true"
      :is-open="!!previewPopupData"
      @close="previewPopupData = null"
    />
    <AddOfferPopup
      v-show="isOfferPopupOpen"
      v-model:data="data"
      v-model:customer-currencies="customerCurrencies"
      :is-open="isOfferPopupOpen"
      :action="action"
      :is-category="true"
      :currency="defaultCurrency"
      @refresh="refresh()"
      @close="onCloseOfferPopup"
    />
    <CatalogDeleteConfirm
      v-show="isCatalogDeletePopupOpen"
      :is-open="isCatalogDeletePopupOpen"
      :data="categoryItem"
      @confirm="deleteCatalogWithReassign"
      @close="onCloseCatalogDeletePopup"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AddOfferPopup from '@/components/Popups/AddOfferPopup.vue';
import offerTemplate from '@/views/Affiliates/Affiliate/helper/offer-template';
import errorHandleMixin from '@/views/mixins/error-hadle';
import offerMixin from '@/views/mixins/offer';
import CatalogDeleteConfirm from './popup/CatalogDeleteConfirm.vue';
import PreviewPopup from './popup/PreviewPopup.vue';

export default {
  components: { AddOfferPopup, PreviewPopup, CatalogDeleteConfirm },
  mixins: [errorHandleMixin, offerMixin],
  props: {
    category: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  emits: ['open-delete', 'open-edit', 'refresh'],

  data() {
    return {
      isDataLoading: false,
      isOfferPopupOpen: false,
      isCatalogDeletePopupOpen: false,
      previewPopupData: null,

      action: '',
      id: '',
      catalogs: [],

      searchQuery: '',
      data: {},

      errors: {},
      confirmMsg: '',
      confirmAction: '',
      confirmTitle: '',
      confirmType: '',
      actionName: '',
      actionFaIcon: '',
      actionIcon: '',
      categoryItem: {
        id: '',
        name: '',
      },
    };
  },

  computed: {
    ...mapGetters({
      brands: 'misc/brands',
      customerCurrencies: 'misc/customerCurrencies',
    }),
    isSuperUser() {
      return this.$store.getters['auth/adminAcl'].is_superuser;
    },
    canEditCatalog() {
      return this.isSuperUser || this.$store.getters['auth/currentAcl']?.offers_catalog_edit === 'allow';
    },
    defaultCurrency() {
      return this.customerCurrencies.find((el) => el.is_default)?.code || 'USD';
    },
  },
  watch: {
    $route() {
      this.id = this.$route.params.id;
      this.searchQuery = '';
      this.getCatalog();
    },
  },
  mounted() {
    this.id = this.$route.params.id;
    this.getCatalog();
  },

  methods: {
    onCloseOfferPopup() {
      this.isOfferPopupOpen = false;
      this.getCatalog();
      this.refresh();
    },
    editOfferPopup(item, action = 'Add') {
      this.action = action;
      let itemNew = {
        category_id: this.id,
        ...item,
      };

      const settings = item.template_settings;
      itemNew = this.$_.merge(itemNew, settings);

      itemNew.payout_rules = itemNew.payout_rules.map((r) => ({
        ...r,
        settings: {
          ...r.settings,
          rev_share: !this.$_.isEmpty(r.settings.rev_share) ? {
            sport_levels: r.settings.rev_share.sport_levels,
            casino_levels: this.$_.isEqual(r.settings.rev_share.sport_levels, r.settings.rev_share.casino_levels)
              ? null : r.settings.rev_share.casino_levels,
          } : null,
          cpa: r.settings?.cpa?.map((cpa) => ({
            currency_code: this.defaultCurrency,
            ...cpa,
          }))?.map(this.genCpaParams) || null,
        },
      }));

      // if (
      //   this.$store.getters['auth/currentAcl'].offers_revshare_edit_settings !== 'allow' // && localStorage.getItem('active-team')
      // ) {
      //   itemNew.payout_rules[0].settings.rev_share.sport_levels = {};
      //   itemNew.payout_rules[0].settings.rev_share.casino_levels = {};
      // }

      delete itemNew.template_settings;
      this.data = itemNew;
      this.isOfferPopupOpen = true;
    },
    addOffer() {
      this.action = 'Add';
      this.editOfferPopup(offerTemplate);
    },
    handlePreview(item) {
      this.previewPopupData = item;
    },
    editCategoryItemHandler(item) {
      this.editOfferPopup(item, 'Edit');
    },
    deleteCategoryItemHandler(item) {
      this.categoryItem.id = item.id;
      this.categoryItem.name = item.name;
      this.checkCatalogBeforeDelete();
    },
    availableBrands(visibility, offerBrands) {
      if (visibility !== 'all') {
        const formatedBrands = [];
        offerBrands.forEach((brand) => {
          formatedBrands.push({
            id: brand.site_id,
            name: brand.site_name,
            favicon_base64: brand.site_favicon,
          });
        });
        return formatedBrands;
      }
      return this.brands;
    },
    getCatalog() {
      this.isDataLoading = true;
      this.$api
        .getAffiliateOffersCatalog({ category_id: this.id, search: this.searchQuery })
        .then((res) => {
          this.catalogs = res.data.payload;
          this.isDataLoading = false;
        });
    },
    refresh() {
      this.$emit('refresh');
    },

    editCategoryHandler() {
      this.$emit('open-edit');
    },

    deleteCategoryHandler() {
      this.$emit('open-delete');
    },

    onConfirm() {
      switch (this.confirmAction) {
      case 'deleteCategoryItem':
        this.deleteCategoryItem();
        break;
      default:
        break;
      }
    },

    deleteCategoryItem() {
      this.isDataLoading = true;
      this.errors = {};

      this.$api
        .deleteAffiliateOffersCatalog(this.categoryItem.id)
        .then(() => {
          this.getCatalog();
          this.$refs.confirm.close();
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeepWith(error.data.errors);
        })
        .finally(() => {
          this.isDataLoading = false;
          this.refresh();
        });
    },

    confirmClose() {
      this.actionIcon = '';
      this.actionFaIcon = '';
      this.categoryItem.name = '';
      this.categoryItem.id = '';
    },

    checkCatalogBeforeDelete() {
      this.$api
        .getAffiliateOffersCatalogBeforeDelete(this.categoryItem.id)
        .then((res) => {
          const needToSetNewOffer = res.data.payload.need_to_set_new_cataloged_offer;
          if (needToSetNewOffer) {
            this.isCatalogDeletePopupOpen = true;
          } else {
            this.showSimpleDeleteConfirm();
          }
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeepWith(error.data.errors);
        });
    },

    showSimpleDeleteConfirm() {
      this.confirmAction = 'deleteCategoryItem';
      this.confirmTitle = this.$t('affiliates.offersCategory.delete_offer_title');
      this.confirmMsg = this.$t('affiliates.offersCategory.delete_offer_msg', { name: this.categoryItem.name });
      this.actionName = this.$t('affiliates.offersCategory.delete_offer_action');
      this.confirmType = 'warning';
      this.actionFaIcon = 'trash-alt';
      this.$refs.confirm.open();
    },

    onCloseCatalogDeletePopup() {
      this.isCatalogDeletePopupOpen = false;
    },

    deleteCatalogWithReassign(newCatalogId) {
      this.errors = {};

      const payload = {
        new_cataloged_affiliate_offer_id: newCatalogId,
      };

      this.$api
        .deleteAffiliateOffersCatalog(this.categoryItem.id, { data: payload })
        .then(() => {
          this.getCatalog();
          this.isCatalogDeletePopupOpen = false;
          this.$refs.confirm.close();
        })
        .catch((error) => {
          this.errors = this.$_.cloneDeepWith(error.data.errors);
        });
    },
  },
};
</script>
